/* =====================================================================
   CYPHER GAMING — cypher-compare.js
   ---------------------------------------------------------------------
   Motore del comparatore. Non contiene dati hardware: quelli stanno
   tutti in cypher-compare-db.js.

   Flusso:
   1. legge la configurazione + i dati del PC corrente (inline, 0 fetch)
   2. carica l'indice dei PC solo quando l'utente apre il selettore
   3. scarica le specifiche complete solo del PC scelto (con cache)
   4. normalizza le specifiche -> profilo hardware -> punteggi
   5. disegna radar, barre, badge e tabella

   Vanilla JS, nessuna dipendenza, nessuna app esterna.
   ===================================================================== */
(function () {
  'use strict';

  var DB = window.CypherCompareDB;
  var root = document.getElementById('cy-cmp');
  if (!root || !DB) return;

  var cfgEl = root.querySelector('[data-cy-cmp-config]');
  if (!cfgEl) return;

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var T = DB.i18n;
  var MAX_SLOTS = Math.min(CFG.maxSlots || 3, 3);
  var COLORS = ['#9DFF00', '#38E1FF', '#FF5CE1'];
  var CACHE_TTL = 30 * 60 * 1000; // 30 minuti

  /* ==================================================================
     1. UTILITY
     ================================================================== */
  function norm(s) {
    return (s == null ? '' : String(s))
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  /** "RTX 5070 Ti" -> "rtx5070ti" : rende il match indipendente da spazi e trattini */
  function flat(s) { return norm(s).replace(/[^a-z0-9]/g, ''); }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  /** Il filtro money può restituire entità HTML (&euro;): le decodifico. */
  var decoder = document.createElement('textarea');
  function decode(s) {
    if (!s || s.indexOf('&') === -1) return s || '';
    decoder.innerHTML = s;
    return decoder.value;
  }
  function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

  /** Nome breve della build: il titolo completo ripete le specifiche
      che sono già nella tabella e rende badge e legenda illeggibili. */
  /** Nome cortissimo per barre e celle della tabella su mobile:
      "CYPHER XENOMORPH BLACK - PC Gaming..." -> "XENOMORPH BLACK" */
  function buildName(t) {
    var s = shortTitle(t).replace(/^cypher\s+/i, '').trim();
    return s.length > 20 ? s.slice(0, 18).trim() + '…' : s;
  }

  function shortTitle(t) {
    var s = String(t == null ? '' : t).split(' - ')[0].trim();
    if (!s) s = String(t || '');
    return s.length > 46 ? s.slice(0, 44).trim() + '…' : s;
  }
  function round(n) { return Math.round(n * 10) / 10; }

  /** Interpolazione su una scala [[soglia, punteggio], ...] */
  function scale(table, v) {
    if (!v) return 0;
    if (v <= table[0][0]) return table[0][1];
    for (var i = 1; i < table.length; i++) {
      if (v <= table[i][0]) {
        var a = table[i - 1], b = table[i];
        var t = (v - a[0]) / (b[0] - a[0]);
        return a[1] + (b[1] - a[1]) * t;
      }
    }
    return table[table.length - 1][1];
  }

  /* ==================================================================
     2. LIVELLO DATI — fetch + cache
     ================================================================== */
  var mem = {};       // cache in memoria (Promise)
  var indexPromise = null;

  function sessionGet(key) {
    try {
      var raw = sessionStorage.getItem('cycmp:' + key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (Date.now() - o.t > CACHE_TTL) return null;
      return o.d;
    } catch (e) { return null; }
  }
  function sessionSet(key, data) {
    try { sessionStorage.setItem('cycmp:' + key, JSON.stringify({ t: Date.now(), d: data })); }
    catch (e) { /* quota piena: pazienza, resta la cache in memoria */ }
  }

  function getProduct(handle) {
    if (mem[handle]) return mem[handle];
    var cached = sessionGet('p:' + handle);
    if (cached) { mem[handle] = Promise.resolve(cached); return mem[handle]; }

    var url = CFG.root + 'products/' + encodeURIComponent(handle) + '?view=' + CFG.dataView;
    var p = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (d) { sessionSet('p:' + handle, d); return d; });

    p.catch(function () { delete mem[handle]; });
    mem[handle] = p;
    return p;
  }

  function getIndex() {
    if (indexPromise) return indexPromise;
    var cached = sessionGet('idx:' + CFG.collection);
    if (cached) { indexPromise = Promise.resolve(cached); return indexPromise; }

    function page(n) {
      var url = CFG.root + 'collections/' + CFG.collection + '?view=' + CFG.indexView + '&page=' + n;
      return fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
    }

    indexPromise = page(1).then(function (d) {
      var all = d.products || [];
      // Oltre 50 prodotti: scarico le pagine restanti in parallelo (max 4).
      if (!d.has_next || d.pages < 2) return all;
      var jobs = [];
      for (var n = 2; n <= Math.min(d.pages, 5); n++) jobs.push(page(n));
      return Promise.all(jobs).then(function (rest) {
        rest.forEach(function (r) { all = all.concat(r.products || []); });
        return all;
      });
    }).then(function (all) {
      sessionSet('idx:' + CFG.collection, all);
      return all;
    });

    indexPromise.catch(function () { indexPromise = null; });
    return indexPromise;
  }

  /* ==================================================================
     3. NORMALIZZAZIONE DELLE SPECIFICHE
     I metafield possono chiamarsi in qualsiasi modo: qui vengono
     ricondotti a chiavi canoniche tramite DB.aliases.
     ================================================================== */
  function aliasKey(rawKey, rawLabel) {
    var s = norm(rawKey + ' ' + (rawLabel || ''));
    var tokens = s.split(/[^a-z0-9]+/).filter(Boolean);
    for (var i = 0; i < DB.aliases.length; i++) {
      var a = DB.aliases[i];
      for (var j = 0; j < a.match.length; j++) {
        var m = a.match[j];
        var hit = m.indexOf(' ') > -1 ? s.indexOf(m) > -1 : tokens.indexOf(m) > -1;
        if (hit) return a;
      }
    }
    return null;
  }

  function aliasByKey(key) {
    for (var i = 0; i < DB.aliases.length; i++) {
      if (DB.aliases[i].key === key) return DB.aliases[i];
    }
    return null;
  }

  /**
   * Estrae le specifiche da titolo + descrizione quando i metafield non ci sono.
   * Il testo arriva già spezzato in frammenti separati da "|".
   * Per ogni frammento: se ha un'etichetta ("CPU: ...") la usa, altrimenti
   * lo classifica con le regole DB.extract. Vince il primo frammento
   * trovato per ciascun componente: nelle schede prodotto la specifica
   * sta sempre prima della prosa di marketing.
   */
  function extractSpecs(raw) {
    var out = [];
    if (!raw) return out;
    var taken = {};

    decode(raw).split('|').forEach(function (seg) {
      var s = seg.replace(/\s+/g, ' ').trim();
      if (!s) return;

      var key = null, val = s;

      // 1. etichetta esplicita ("Raffreddamento: Dissipatore a liquido 360mm")
      var m = s.match(/^([A-Za-zÀ-ÿ0-9 .\/-]{2,24}?)\s*:\s*(.+)$/);
      if (m) {
        var a = aliasKey(m[1], '');
        val = m[2];
        if (a) key = a.key;
      }

      // 2. taglio la parte descrittiva dopo il trattino lungo
      val = val.split(/\s[–—-]\s/)[0].trim().replace(/[.;]+$/, '');

      // 3. nessuna etichetta utile: classifico dal contenuto
      if (!key) {
        for (var i = 0; i < DB.extract.length; i++) {
          if (DB.extract[i].re.test(val)) { key = DB.extract[i].key; break; }
        }
      }

      if (!key || taken[key]) return;

      // 4. tolgo l'etichetta ripetuta in testa al valore
      var pre = (DB.stripPrefix || {})[key] || [];
      for (var j = 0; j < pre.length; j++) {
        var rx = new RegExp('^' + pre[j].replace(/ /g, '\\s+') + '\\s*:?\\s+', 'i');
        if (rx.test(val)) { val = val.replace(rx, '').trim(); break; }
      }

      if (val.length < 2 || val.length > (DB.extractMaxLen || 70)) return;

      taken[key] = 1;
      out.push({ k: key, l: '', v: val, s: 'auto' });
    });

    return out;
  }

  function canonicalize(specs, raw) {
    var map = {}, order = [];

    function add(sp) {
      var a, key;
      if (sp.s === 'auto') { key = sp.k; a = aliasByKey(key); }
      else { a = aliasKey(sp.k, sp.l); key = a ? a.key : 'x_' + flat(sp.k); }
      if (map[key]) return; // vince la prima fonte: metafield > testo
      map[key] = {
        key: key,
        label: a ? a.label : (sp.l || sp.k),
        value: decode(sp.v),
        src: sp.s
      };
      order.push(key);
    }

    (specs || []).forEach(add);
    extractSpecs(raw).forEach(add);
    return { map: map, order: order, raw: raw || '' };
  }

  function specValue(c, key) { return c.map[key] ? c.map[key].value : ''; }
  function allText(c) {
    return c.order.map(function (k) { return c.map[k].value; }).join(' | ');
  }

  /* ==================================================================
     4. RICONOSCIMENTO COMPONENTI
     ================================================================== */
  function findIn(table, text) {
    var f = flat(text);
    if (!f) return null;
    for (var i = 0; i < table.length; i++) {
      for (var j = 0; j < table[i].m.length; j++) {
        if (f.indexOf(table[i].m[j]) > -1) return table[i];
      }
    }
    return null;
  }

  /** Stima di riserva per GPU non ancora in tabella (es. modello nuovo). */
  function guessGPU(text) {
    var f = flat(text);
    var m = f.match(/rtx(\d)0(\d)0(ti|super)?/);
    if (m) {
      var gen = parseInt(m[1], 10), tier = parseInt(m[2], 10);
      var base = 12 + tier * 9 + (gen - 3) * 6 + (m[3] ? 5 : 0);
      return { n: 'GPU non in tabella', raster: clamp(base, 12, 98), rt: clamp(base - 4, 8, 96), vram: 8, tdp: 220, guess: true };
    }
    var a = f.match(/rx(\d)(\d)00(xtx|xt)?/);
    if (a) {
      var t2 = parseInt(a[2], 10);
      var b2 = 14 + t2 * 8 + (a[3] === 'xtx' ? 8 : a[3] === 'xt' ? 4 : 0);
      return { n: 'GPU non in tabella', raster: clamp(b2, 12, 92), rt: clamp(b2 - 12, 8, 80), vram: 12, tdp: 240, guess: true };
    }
    return null;
  }

  /**
   * Stima di riserva per CPU non ancora in tabella (modelli usciti dopo
   * l'ultimo aggiornamento del DB). Legge generazione e fascia dal numero:
   * "9850X3D" -> generazione 9, fascia 8, con cache 3D.
   * Meglio di un valore neutro, ma resta una stima: la confidenza scende
   * e la pagina avvisa che il punteggio è stimato.
   */
  function guessCPU(text) {
    var f = flat(text);

    // AMD Ryzen: 4 cifre = generazione + fascia (es. 9850 -> gen 9, fascia 8)
    var a = f.match(/([1-9])([0-9])00(x3d|xt|x|g|f|e)?/);
    if (a && (f.indexOf('ryzen') > -1 || f.indexOf('amd') > -1)) {
      var gen = parseInt(a[1], 10), tier = parseInt(a[2], 10);
      var x3d = a[3] === 'x3d';
      var single = clamp(48 + tier * 4.5 + (gen - 5) * 2.5, 40, 98);
      var cores = tier >= 9 ? 16 : tier >= 8 ? 8 : tier >= 7 ? 8 : 6;
      var multi = clamp(single * 0.55 + cores * 2.2, 30, 98);
      return {
        n: 'CPU non in tabella', single: single, multi: multi,
        gaming: clamp(x3d ? single + 5 : single - 8, 30, 100),
        cores: cores, tdp: tier >= 9 ? 170 : 105, guess: true
      };
    }

    // Intel Core: le prime due cifre sono la generazione (14700 -> 14a gen)
    var i = f.match(/i([3579])(\d{2})(\d{3})(kf|k|f)?/);
    if (i) {
      var fam = parseInt(i[1], 10), igen = parseInt(i[2], 10);
      var base = 46 + fam * 5 + clamp((igen - 10) * 1.6, 0, 12);
      return {
        n: 'CPU non in tabella', single: clamp(base, 40, 98),
        multi: clamp(base - 6 + fam * 2, 30, 98), gaming: clamp(base - 4, 30, 96),
        cores: fam >= 9 ? 20 : fam >= 7 ? 16 : 10, tdp: i[4] ? 180 : 120, guess: true
      };
    }
    return null;
  }

  function parseRAM(text, fallbackText) {
    var s = norm(text);
    var out = { gb: 0, modules: 0, type: '', speed: 0 };
    var kit = s.match(/(\d)\s*[x×]\s*(\d{1,3})\s*gb/);
    if (kit) { out.modules = +kit[1]; out.gb = +kit[1] * +kit[2]; }
    else {
      var one = s.match(/(\d{1,3})\s*gb/);
      if (one) { out.gb = +one[1]; out.modules = out.gb >= 32 ? 2 : 2; }
    }
    if (s.indexOf('ddr5') > -1) out.type = 'DDR5';
    else if (s.indexOf('ddr4') > -1) out.type = 'DDR4';
    // Frequenza: prima nel valore RAM, poi in tutto il testo del prodotto
    [s, norm(fallbackText || '')].forEach(function (hay) {
      if (out.speed || !hay) return;
      var sp = hay.match(/(\d{4,5})\s*(mhz|mt\/s|mts)/);
      if (sp && +sp[1] >= 2000 && +sp[1] <= 9000) out.speed = +sp[1];
    });
    // Ancora niente: la deduco dalla generazione invece di penalizzare
    if (!out.speed && out.type) {
      out.speed = DB.heuristics.ramSpeedFallback[out.type] || 0;
      out.speedGuessed = true;
    }
    return out;
  }

  function parseStorage(text) {
    var s = norm(text);
    var gb = 0, re = /(\d+(?:[.,]\d+)?)\s*(tb|gb)/g, m, drives = 0;
    while ((m = re.exec(s)) !== null) {
      var v = parseFloat(m[1].replace(',', '.'));
      gb += m[2] === 'tb' ? v * 1000 : v;
      drives++;
    }
    var type = 'sata';
    if (s.indexOf('gen5') > -1 || s.indexOf('pcie 5') > -1) type = 'gen5';
    else if (s.indexOf('gen4') > -1 || s.indexOf('pcie 4') > -1) type = 'gen4';
    else if (s.indexOf('gen3') > -1 || s.indexOf('pcie 3') > -1) type = 'gen3';
    else if (s.indexOf('nvme') > -1 || s.indexOf('m.2') > -1) type = 'nvme';
    else if (s.indexOf('hdd') > -1 || s.indexOf('hard disk') > -1) type = 'hdd';
    return { gb: gb, drives: drives || 1, type: type };
  }

  function parsePSU(text) {
    var s = norm(text);
    var w = s.match(/(\d{3,4})\s*w/);
    var eff = '';
    ['titanium', 'platinum', 'gold', 'silver', 'bronze'].forEach(function (e) {
      if (!eff && s.indexOf(e) > -1) eff = e;
    });
    return { watt: w ? +w[1] : 0, eff: eff, modular: s.indexOf('modular') > -1 };
  }

  function parseCooling(text) {
    var f = flat(text);
    var table = DB.heuristics.cooling;
    for (var i = 0; i < table.length; i++) {
      for (var j = 0; j < table[i].m.length; j++) {
        if (f.indexOf(flat(table[i].m[j])) > -1) return { score: table[i].s, raw: text };
      }
    }
    return { score: text ? DB.heuristics.coolingDefault : 0, raw: text };
  }

  function parseChipset(text) {
    var f = flat(text);
    var keys = Object.keys(DB.chipset).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      if (f.indexOf(keys[i]) > -1) return { key: keys[i].toUpperCase(), score: DB.chipset[keys[i]] };
    }
    return { key: '', score: 0 };
  }

  /* ==================================================================
     5. PROFILO + PUNTEGGI
     ================================================================== */
  function buildProfile(c) {
    var text = allText(c);
    var gpuTxt = specValue(c, 'gpu') || text;
    var cpuTxt = specValue(c, 'cpu') || text;

    var gpu = findIn(DB.gpu, gpuTxt) || guessGPU(gpuTxt);
    var cpu = findIn(DB.cpu, cpuTxt) || guessCPU(cpuTxt);

    // VRAM dichiarata nella stringa GPU (es. "RTX 5070 12GB") ha priorità
    var vramMatch = norm(gpuTxt).match(/(\d{1,2})\s*gb/);
    var vram = gpu ? gpu.vram : 8;
    if (vramMatch) vram = +vramMatch[1];

    return {
      gpu: gpu, cpu: cpu, vram: vram,
      ram: parseRAM(specValue(c, 'ram') || text, (c.raw || '') + ' ' + text),
      storage: parseStorage(specValue(c, 'storage') || ''),
      psu: parsePSU(specValue(c, 'psu') || ''),
      cooling: parseCooling(specValue(c, 'cooling') || ''),
      chipset: parseChipset(specValue(c, 'chipset') + ' ' + specValue(c, 'motherboard')),
      caseTxt: specValue(c, 'case'),
      known: !!(gpu && !gpu.guess) + !!cpu
    };
  }

  function computeScores(p) {
    var H = DB.heuristics;
    var s = {};

    /* --- CPU: multi, single e resa in gioco --- */
    s.cpu = p.cpu
      ? clamp(p.cpu.single * H.cpuWeights.single + p.cpu.multi * H.cpuWeights.multi + p.cpu.gaming * H.cpuWeights.gaming, 0, 100)
      : 50;

    /* --- GPU: pura potenza di rasterizzazione --- */
    s.gpu = p.gpu ? clamp(p.gpu.raster, 0, 100) : 50;

    /* --- GAMING: la GPU comanda, la CPU limita, la VRAM abilita il 4K --- */
    var vramScore = scale([[6, 30], [8, 55], [12, 78], [16, 92], [24, 100], [32, 100]], p.vram);
    var rt = p.gpu ? p.gpu.rt : 50;
    var cpuGame = p.cpu ? p.cpu.gaming : 60;
    s.gaming = clamp(
      (p.gpu ? p.gpu.raster : 50) * 0.66 + cpuGame * 0.15 + vramScore * 0.11 + rt * 0.08,
      0, 100
    );
    // Collo di bottiglia: CPU molto più debole della GPU -> penalità reale
    if (p.cpu && p.gpu && p.gpu.raster - p.cpu.gaming > 25) {
      s.gaming = clamp(s.gaming - (p.gpu.raster - p.cpu.gaming - 25) * 0.25, 0, 100);
    }

    /* --- MEMORIA RAM: quantità, velocità, piattaforma ---
       La capacità pesa più di tutto: è quella che decide se puoi
       giocare e streammare insieme o tenere aperto un progetto video.
       Il modulo singolo viene penalizzato: dimezza la banda reale. */
    var ramCap = scale(H.ramCapacity, p.ram.gb);
    var ramSpd = scale(H.ramSpeed, p.ram.speed);
    if (p.ram.type === 'DDR5') ramSpd = clamp(ramSpd + H.ddr5Bonus, 0, 100);
    var ramPlat = p.ram.type === 'DDR5' ? H.ramPlatform.ddr5
                : p.ram.type === 'DDR4' ? H.ramPlatform.ddr4
                : H.ramPlatform.unknown;
    if (p.ram.modules === 1) ramPlat -= H.ramPlatform.singleChannel;
    s.ram = clamp(
      ramCap * H.ramWeights.capacity + ramSpd * H.ramWeights.speed + clamp(ramPlat, 0, 100) * H.ramWeights.platform,
      0, 100
    );

    /* --- STORAGE: capacità + interfaccia --- */
    var stCap = scale(H.storageCapacity, p.storage.gb);
    var stType = H.storageType[p.storage.type] || 60;
    s.storage = clamp(stCap * H.storageWeights.capacity + stType * H.storageWeights.type, 0, 100);

    /* --- ALIMENTAZIONE: adeguatezza + taglia + efficienza ---
       "Adeguato" non vuol dire "il più grosso": un 1000W su una build
       da 300W non rende il PC migliore, per questo la curva si appiattisce
       oltre 1.5x. Sotto 1.0x invece il punteggio crolla, perché è un
       alimentatore sottodimensionato e si vede sotto carico. */
    var load = (p.cpu ? p.cpu.tdp : 120) + (p.gpu ? p.gpu.tdp : 200) + H.psu.overhead;
    var adequacy = p.psu.watt ? scale(H.psu.adequacy, p.psu.watt / load) : 55;
    var wattClass = p.psu.watt ? scale(H.psu.wattClass, p.psu.watt) : 55;
    var effScore = p.psu.eff ? (H.psu.efficiency[p.psu.eff] || H.psu.efficiencyUnknown) : H.psu.efficiencyUnknown;
    s.psu = clamp(
      adequacy * H.psu.weights.adequacy + wattClass * H.psu.weights.watt + effScore * H.psu.weights.efficiency,
      0, 100
    );

    /* --- RAFFREDDAMENTO: non è più un asse del radar, ma serve
       a decidere il vincitore della riga in tabella. --- */
    var cool = p.cooling.score || H.coolingDefault;
    if (p.cpu && p.cpu.tdp >= 170 && cool < 80) cool -= 12;
    if (norm(p.caseTxt).indexOf('mesh') > -1 || norm(p.caseTxt).indexOf('airflow') > -1) cool += 4;
    s.cooling = clamp(cool, 0, 100);

    /* --- PUNTEGGIO COMPLESSIVO pesato --- */
    var num = 0, den = 0;
    DB.categories.forEach(function (cat) {
      num += (s[cat.key] || 0) * cat.weight;
      den += cat.weight;
    });
    s.overall = clamp(num / den, 0, 100);
    return s;
  }

  /** Confidenza: quanto il punteggio si basa su dati riconosciuti. */
  function confidence(p) {
    var pts = 0;
    if (p.gpu && !p.gpu.guess) pts += 2; else if (p.gpu) pts += 1;
    if (p.cpu && !p.cpu.guess) pts += 2; else if (p.cpu) pts += 1;
    if (p.ram.gb) pts += 1;
    if (p.storage.gb) pts += 1;
    return pts / 6;
  }

  function analyze(data) {
    var c = canonicalize(data.specs, data.raw);
    var prof = buildProfile(c);
    return {
      data: data,
      canon: c,
      profile: prof,
      scores: computeScores(prof),
      confidence: confidence(prof)
    };
  }

  /* ==================================================================
     6. STATO
     ================================================================== */
  var state = {
    handles: [CFG.current.handle],   // slot 0 = PC della pagina
    items: {},                       // handle -> analisi
    onlyDiff: false
  };
  state.items[CFG.current.handle] = analyze(CFG.current);

  /* ==================================================================
     7. RENDER
     ================================================================== */
  var els = {
    slots:   root.querySelector('[data-cy-slots]'),
    verdict: root.querySelector('[data-cy-verdict]'),
    radar:   root.querySelector('[data-cy-radar]'),
    bars:    root.querySelector('[data-cy-bars]'),
    table:   root.querySelector('[data-cy-table]'),
    tools:   root.querySelector('[data-cy-tools]'),
    note:    root.querySelector('[data-cy-note]'),
    dialog:  root.querySelector('[data-cy-dialog]'),
    list:    root.querySelector('[data-cy-list]'),
    search:  root.querySelector('[data-cy-search]')
  };

  function loaded() {
    return state.handles.map(function (h) { return state.items[h]; }).filter(Boolean);
  }

  /* ---------- 7a. Slot / testate ---------- */
  function renderSlots() {
    var html = '';
    for (var i = 0; i < MAX_SLOTS; i++) {
      var h = state.handles[i];
      var it = h ? state.items[h] : null;

      if (!h && i > state.handles.length) continue;            // un solo slot vuoto alla volta
      if (!h && i === 0) continue;

      if (it) {
        var d = it.data;
        var rest = d.title.indexOf(' - ') > -1 ? d.title.split(' - ').slice(1).join(' - ') : '';
        html += '<article class="cy-slot' + (i === 0 ? ' is-locked' : '') + '" style="--slot:' + COLORS[i] + '">' +
          '<span class="cy-slot__tag">' + (i === 0 ? esc(T.thisPage) : 'PC ' + (i + 1)) + '</span>' +
          // __top: in colonna su desktop, in riga su mobile (foto piccola a sinistra)
          '<div class="cy-slot__top">' +
            '<div class="cy-slot__img">' + (d.img ? '<img src="' + esc(d.img) + '" alt="" loading="lazy" width="160" height="160">' : '') + '</div>' +
            '<div class="cy-slot__id">' +
              '<h3 class="cy-slot__name">' + esc(shortTitle(d.title)) + '</h3>' +
              (rest ? '<div class="cy-slot__sub">' + esc(rest) + '</div>' : '') +
              '<div class="cy-slot__price">' + esc(decode(d.price_f)) + '</div>' +
            '</div>' +
          '</div>' +
          '<div class="cy-slot__score"><b>' + Math.round(it.scores.overall) + '</b><small>/100 punteggio Cypher</small></div>' +
          '<div class="cy-slot__act">' +
            (i === 0
              ? '<a class="cy-mini" href="#cy-cmp-table">Vedi specifiche</a>'
              : '<button type="button" class="cy-mini" data-cy-pick="' + i + '">' + esc(T.change) + '</button>' +
                '<button type="button" class="cy-mini cy-mini--x" data-cy-drop="' + i + '">' + esc(T.remove) + '</button>' +
                '<a class="cy-mini cy-mini--go" href="' + esc(d.url) + '">Vedi PC</a>') +
          '</div>' +
        '</article>';
      } else {
        html += '<button type="button" class="cy-slot cy-slot--empty" data-cy-pick="' + i + '">' +
          '<span class="cy-slot__plus" aria-hidden="true">+</span>' +
          '<span class="cy-slot__cta">' + esc(i === 1 ? T.pickCta : T.addThird) + '</span>' +
          '<span class="cy-slot__hint">Confronta con un\'altra build Cypher</span>' +
        '</button>';
      }
    }
    els.slots.innerHTML = html;
  }

  /* ---------- 7b. Verdetto: badge + differenza percentuale ---------- */
  function renderVerdict() {
    var list = loaded();
    if (list.length < 2) { els.verdict.innerHTML = ''; els.verdict.hidden = true; return; }
    els.verdict.hidden = false;

    // Indice qualità/prezzo: prestazioni per euro, normalizzato sul migliore
    var vals = list.map(function (it) {
      return it.data.price > 0 ? it.scores.overall / (it.data.price / 100) : 0;
    });
    var maxVal = Math.max.apply(null, vals);
    list.forEach(function (it, i) { it.value = maxVal ? (vals[i] / maxVal) * 100 : 0; });

    /** Vincitore di una metrica. Restituisce -1 se il primo posto è
        condiviso o il margine è irrilevante: in quel caso il badge
        non viene mostrato affatto, invece di premiare a caso il primo. */
    function best(fn, minDelta) {
      var vals = list.map(fn);
      var max = Math.max.apply(null, vals);
      var tol = minDelta == null ? 1 : minDelta;
      var tied = vals.filter(function (v) { return max - v < tol; }).length;
      return tied > 1 ? -1 : vals.indexOf(max);
    }

    var iPower  = best(function (x) { return x.scores.overall; });
    var iGaming = best(function (x) { return x.scores.gaming; });
    var iValue  = best(function (x) { return x.value; }, 2);
    var iRam    = best(function (x) { return x.scores.ram; });
    var iStor   = best(function (x) { return x.scores.storage; });
    var iCheap  = best(function (x) { return -x.data.price; }, 0.5);

    var badges = [
      { i: iPower,  t: T.badges.power },
      { i: iGaming, t: T.badges.gaming },
      { i: iValue,  t: T.badges.value },
      { i: iRam,    t: T.badges.ram },
      { i: iStor,   t: T.badges.storage },
      { i: iCheap,  t: T.badges.cheap }
    ].filter(function (b) { return b.i >= 0; });

    // Delta complessivo fra il migliore e il secondo
    var sorted = list.slice().sort(function (a, b) { return b.scores.overall - a.scores.overall; });
    var delta = sorted[1].scores.overall > 0
      ? ((sorted[0].scores.overall - sorted[1].scores.overall) / sorted[1].scores.overall) * 100
      : 0;

    var html = '<div class="cy-verdict__delta">';
    if (delta < 1.5) {
      // Build equivalenti: dirlo apertamente vale più di un numero senza senso
      html += '<span class="cy-verdict__num">≈</span>' +
        '<span class="cy-verdict__txt">Sul punteggio complessivo queste build sono <b>equivalenti</b>. ' +
        'La differenza sta nei singoli componenti: guarda il radar e la tabella qui sotto.</span>';
    } else {
      html += '<span class="cy-verdict__num">+' + round(delta) + '%</span>' +
        '<span class="cy-verdict__txt">di punteggio complessivo per <b>' + esc(shortTitle(sorted[0].data.title)) +
        '</b> rispetto a ' + esc(shortTitle(sorted[1].data.title)) + '</span>';
    }
    html += '</div><div class="cy-verdict__badges">';

    badges.forEach(function (b) {
      html += '<span class="cy-badge" style="--slot:' + COLORS[b.i] + '">' +
        '<i aria-hidden="true"></i><b>' + esc(b.t) + '</b>' +
        '<em>' + esc(shortTitle(list[b.i].data.title)) + '</em></span>';
    });
    els.verdict.innerHTML = html + '</div>';
  }

  /* ---------- 7c. Radar SVG ----------
     Le dimensioni del viewBox seguono la larghezza reale del contenitore:
     così un'unità SVG vale sempre circa un pixel CSS e le etichette
     restano leggibili a 340px come a 520px, senza rimpicciolirsi. */
  function renderRadar() {
    var list = loaded();
    var cats = DB.categories;
    var n = cats.length;

    var avail = els.radar.clientWidth || 460;
    var W = Math.round(clamp(avail, 300, 520));
    var H = Math.round(W * 0.84);
    var cx = W / 2, cy = H / 2;
    var R = Math.round(W * 0.285);          // raggio del grafico
    var LR = R + Math.round(W * 0.055);     // raggio delle etichette

    function ptR(i, radius) {
      var ang = (Math.PI * 2 * i) / n - Math.PI / 2;
      return [cx + Math.cos(ang) * radius, cy + Math.sin(ang) * radius];
    }
    function pt(i, v) { return ptR(i, (clamp(v, 0, 100) / 100) * R); }
    function poly(vals) {
      return vals.map(function (v, i) { var p = pt(i, v); return round(p[0]) + ',' + round(p[1]); }).join(' ');
    }

    var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="Confronto per categoria">';

    // griglia
    [25, 50, 75, 100].forEach(function (lv) {
      svg += '<polygon class="cy-radar__grid" points="' + poly(cats.map(function () { return lv; })) + '"/>';
    });
    // assi + etichette
    cats.forEach(function (c, i) {
      var p = ptR(i, R);
      svg += '<line class="cy-radar__axis" x1="' + cx + '" y1="' + cy + '" x2="' + round(p[0]) + '" y2="' + round(p[1]) + '"/>';
      var lp = ptR(i, LR);
      var anchor = Math.abs(lp[0] - cx) < W * 0.03 ? 'middle' : (lp[0] > cx ? 'start' : 'end');
      svg += '<text class="cy-radar__label" x="' + round(lp[0]) + '" y="' + round(lp[1] + 4) + '" text-anchor="' + anchor + '">' + esc(c.short) + '</text>';
    });
    // serie
    list.forEach(function (it, idx) {
      var i0 = state.handles.indexOf(it.data.handle);
      var col = COLORS[i0 < 0 ? idx : i0];
      var vals = cats.map(function (c) { return it.scores[c.key] || 0; });
      svg += '<polygon class="cy-radar__area" points="' + poly(vals) + '" style="--slot:' + col + '"/>';
      vals.forEach(function (v, i) {
        var p = pt(i, v);
        svg += '<circle class="cy-radar__dot" cx="' + round(p[0]) + '" cy="' + round(p[1]) + '" r="3.2" style="--slot:' + col + '"/>';
      });
    });
    svg += '</svg>';

    var legend = '<ul class="cy-legend">' + list.map(function (it) {
      var i0 = state.handles.indexOf(it.data.handle);
      return '<li style="--slot:' + COLORS[i0] + '"><i aria-hidden="true"></i>' + esc(shortTitle(it.data.title)) + '</li>';
    }).join('') + '</ul>';

    els.radar.innerHTML = svg + legend;
    radarWidth = avail;
  }

  /* ---------- 7d. Barre per categoria ----------
     Ogni barra porta il nome della build accanto al colore: su mobile
     la legenda del radar è lontana e il solo colore non basta. */
  function renderBars() {
    var list = loaded();
    var html = '';
    DB.categories.forEach(function (cat) {
      var best = 0;
      list.forEach(function (it, i) { if (it.scores[cat.key] > list[best].scores[cat.key]) best = i; });
      html += '<div class="cy-cat"><div class="cy-cat__h">' + esc(cat.label) + '</div>';
      list.forEach(function (it, i) {
        var i0 = state.handles.indexOf(it.data.handle);
        var v = Math.round(it.scores[cat.key] || 0);
        html += '<div class="cy-bar' + (list.length > 1 && i === best ? ' is-best' : '') + '" style="--slot:' + COLORS[i0] + '">' +
          '<span class="cy-bar__n">' + esc(buildName(it.data.title)) + '</span>' +
          '<span class="cy-bar__track"><span class="cy-bar__fill" style="width:' + v + '%"></span></span>' +
          '<span class="cy-bar__v">' + v + '</span>' +
        '</div>';
      });
      html += '</div>';
    });
    els.bars.innerHTML = html;
  }

  /* ---------- 7e. Tabella comparativa ---------- */
  /** Metrica confrontabile per riga: serve a decidere il "vincitore". */
  function rowMetric(it, key) {
    var s = it.scores, p = it.profile;
    switch (key) {
      case 'cpu': return s.cpu;
      case 'gpu': return s.gpu;
      case 'ram': return s.ram;
      case 'storage': return s.storage;
      case 'cooling': return s.cooling;
      case 'psu': return s.psu;
      case 'chipset': return p.chipset.score;
      default: return null;
    }
  }

  function renderTable() {
    var list = loaded();
    var meta;

    /** Una cella della tabella. Il valore sta dentro uno <span> perché su
        mobile la cella diventa una griglia "nome | valore": senza wrapper
        il triangolo del vincitore finirebbe in una colonna a sé. */
    function cell(valueHtml, i, isBest, winLabel) {
      return '<td class="' + (isBest ? 'is-best' : '') + '"' +
        ' style="--slot:' + meta[i].col + '" data-name="' + esc(meta[i].name) + '">' +
        '<span class="cy-tbl__v">' + valueHtml +
        (isBest ? '<i class="cy-tbl__win" aria-label="' + winLabel + '"></i>' : '') +
        '</span></td>';
    }

    // Unione delle chiavi: prima l'ordine consigliato, poi le extra
    var keys = [], seen = {};
    DB.order.forEach(function (k) {
      if (list.some(function (it) { return it.canon.map[k]; })) { keys.push(k); seen[k] = 1; }
    });
    list.forEach(function (it) {
      it.canon.order.forEach(function (k) { if (!seen[k]) { keys.push(k); seen[k] = 1; } });
    });

    var cols = list.length;

    // Colore e nome breve di ogni colonna: su mobile la tabella diventa
    // un elenco di blocchi e ogni cella deve dire da sola a chi appartiene
    // (il nome viene stampato dal CSS con content: attr(data-name)).
    meta = list.map(function (it) {
      var i0 = state.handles.indexOf(it.data.handle);
      return { col: COLORS[i0], name: buildName(it.data.title) };
    });

    var html = '<table class="cy-tbl" style="--cols:' + cols + '"><thead><tr><th scope="col">Componente</th>';
    list.forEach(function (it, i) {
      html += '<th scope="col" style="--slot:' + meta[i].col + '"><span>' + esc(shortTitle(it.data.title)) + '</span></th>';
    });
    html += '</tr></thead><tbody>';

    // Riga prezzo, sempre in cima
    var prices = list.map(function (it) { return it.data.price; });
    var minPrice = Math.min.apply(null, prices);
    html += '<tr class="cy-tbl__price"><th scope="row">Prezzo</th>';
    list.forEach(function (it, i) {
      var best = cols > 1 && it.data.price === minPrice;
      html += cell(esc(decode(it.data.price_f)), i, best, 'più conveniente');
    });
    html += '</tr>';

    keys.forEach(function (k) {
      var cells = list.map(function (it) { return it.canon.map[k] ? it.canon.map[k].value : '—'; });
      var same = cells.every(function (v) { return norm(v) === norm(cells[0]); });
      if (state.onlyDiff && same && cols > 1) return;

      var label = '';
      for (var i = 0; i < list.length && !label; i++) {
        if (list[i].canon.map[k]) label = list[i].canon.map[k].label;
      }

      // vincitore di riga (solo per componenti misurabili)
      var bestIdx = -1;
      if (cols > 1 && DB.comparable.indexOf(k) > -1 && !same) {
        var metrics = list.map(function (it) { return rowMetric(it, k); });
        if (metrics.every(function (m) { return typeof m === 'number' && !isNaN(m); })) {
          bestIdx = metrics.indexOf(Math.max.apply(null, metrics));
          if (metrics.filter(function (m) { return m === metrics[bestIdx]; }).length > 1) bestIdx = -1;
        }
      }

      html += '<tr' + (same && cols > 1 ? ' class="is-same"' : '') + '><th scope="row">' + esc(label) + '</th>';
      cells.forEach(function (v, i) {
        html += cell(esc(v), i, i === bestIdx, 'migliore');
      });
      html += '</tr>';
    });

    html += '</tbody></table>';
    els.table.innerHTML = html;
  }

  /* ---------- 7f. Nota sull'affidabilità del punteggio ---------- */
  function renderNote() {
    var low = loaded().some(function (it) { return it.confidence < 0.7; });
    els.note.hidden = !low;
    if (low) els.note.textContent = T.unknownData;
  }

  function render() {
    renderSlots();
    var multi = loaded().length > 1;
    root.classList.toggle('is-comparing', multi);
    if (multi) { renderVerdict(); renderRadar(); renderBars(); renderNote(); }
    else { els.verdict.hidden = true; els.note.hidden = true; }
    renderTable();
    els.tools.hidden = !multi;
    syncURL();
  }

  /* ==================================================================
     8. URL CONDIVISIBILE
     ================================================================== */
  function syncURL() {
    var others = state.handles.slice(1).filter(Boolean);
    var url = new URL(window.location.href);
    if (others.length) url.searchParams.set('vs', others.join(','));
    else url.searchParams.delete('vs');
    history.replaceState(null, '', url.toString());
  }

  function readURL() {
    var v = new URL(window.location.href).searchParams.get('vs');
    if (!v) return [];
    return v.split(',').map(function (s) { return s.trim(); })
      .filter(function (s) { return s && s !== CFG.current.handle; })
      .slice(0, MAX_SLOTS - 1);
  }

  /* ==================================================================
     9. CARICAMENTO DI UNO SLOT
     ================================================================== */
  function setSlot(index, handle) {
    if (!handle) return Promise.resolve();
    if (state.handles.indexOf(handle) > -1) return Promise.resolve();
    root.classList.add('is-loading');
    return getProduct(handle).then(function (d) {
      state.items[handle] = analyze(d);
      state.handles[index] = handle;
      root.classList.remove('is-loading');
      render();
    }).catch(function () {
      root.classList.remove('is-loading');
      els.note.hidden = false;
      els.note.textContent = T.error;
    });
  }

  function dropSlot(index) {
    state.handles.splice(index, 1);
    render();
  }

  /* ==================================================================
     10. SELETTORE (dialog)
     ================================================================== */
  var radarWidth = 0;
  var pickTarget = 1;
  var indexData = null;

  function openPicker(index) {
    pickTarget = index;
    els.search.value = '';
    els.list.innerHTML = '<li class="cy-pick__msg">' + esc(T.loading) + '</li>';
    if (typeof els.dialog.showModal === 'function') els.dialog.showModal();
    else els.dialog.setAttribute('open', '');

    getIndex().then(function (items) {
      indexData = items;
      drawPicker('');
      els.search.focus();
    }).catch(function () {
      els.list.innerHTML = '<li class="cy-pick__msg">' + esc(T.error) + '</li>';
    });
  }

  /* Anteprima nel selettore: usa le stesse fonti del confronto,
     metafield se ci sono, altrimenti titolo/descrizione. */
  function shortSpec(p, key) {
    if (!p._canon) p._canon = canonicalize(p.specs, p.raw);
    return p._canon.map[key] ? p._canon.map[key].value : '';
  }

  function drawPicker(q) {
    var qq = norm(q);
    var rows = (indexData || []).filter(function (p) {
      if (state.handles.indexOf(p.handle) > -1) return false;
      if (!qq) return true;
      var hay = norm(p.title + ' ' + (p.raw || '') + ' ' + (p.specs || []).map(function (s) { return s.v; }).join(' '));
      return qq.split(' ').every(function (w) { return hay.indexOf(w) > -1; });
    });

    if (!rows.length) {
      els.list.innerHTML = '<li class="cy-pick__msg">' + esc(T.empty) + '</li>';
      return;
    }

    els.list.innerHTML = rows.map(function (p) {
      var gpu = shortSpec(p, 'gpu');
      var cpu = shortSpec(p, 'cpu');
      return '<li><button type="button" class="cy-pick__i" data-cy-choose="' + esc(p.handle) + '">' +
        '<span class="cy-pick__img">' + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="72" height="72">' : '') + '</span>' +
        '<span class="cy-pick__b">' +
          '<span class="cy-pick__t">' + esc(shortTitle(p.title)) + '</span>' +
          '<span class="cy-pick__s">' + esc([gpu, cpu].filter(Boolean).join(' · ')) + '</span>' +
        '</span>' +
        '<span class="cy-pick__p">' + esc(decode(p.price_f)) + '</span>' +
      '</button></li>';
    }).join('');
  }

  function closePicker() {
    if (typeof els.dialog.close === 'function' && els.dialog.open) els.dialog.close();
    else els.dialog.removeAttribute('open');
  }

  /* ==================================================================
     11. EVENTI
     ================================================================== */
  root.addEventListener('click', function (e) {
    // Click sullo sfondo del dialog: chiude
    if (e.target === els.dialog) { closePicker(); return; }

    var t = e.target.closest('[data-cy-pick],[data-cy-drop],[data-cy-choose],[data-cy-close],[data-cy-share],[data-cy-diff]');
    if (!t) return;

    if (t.hasAttribute('data-cy-pick')) { openPicker(+t.getAttribute('data-cy-pick')); return; }
    if (t.hasAttribute('data-cy-drop')) { dropSlot(+t.getAttribute('data-cy-drop')); return; }
    if (t.hasAttribute('data-cy-close')) { closePicker(); return; }

    if (t.hasAttribute('data-cy-choose')) {
      var h = t.getAttribute('data-cy-choose');
      closePicker();
      setSlot(pickTarget, h);
      return;
    }

    if (t.hasAttribute('data-cy-diff')) {
      state.onlyDiff = !state.onlyDiff;
      t.setAttribute('aria-pressed', String(state.onlyDiff));
      renderTable();
      return;
    }

    if (t.hasAttribute('data-cy-share')) {
      var url = window.location.href;
      var done = function () {
        var old = t.textContent;
        t.textContent = T.shared;
        setTimeout(function () { t.textContent = old; }, 1800);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(done).catch(function () {});
      else { window.prompt('Copia il link', url); }
    }
  });

  // Ricerca nel selettore
  var searchTimer;
  els.search.addEventListener('input', function () {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(function () { drawPicker(els.search.value); }, 120);
  });

  // Prefetch: appena l'utente passa sopra una voce, scarico i dati
  els.list.addEventListener('mouseover', function (e) {
    var b = e.target.closest('[data-cy-choose]');
    if (b) getProduct(b.getAttribute('data-cy-choose'));
  });

  // Prefetch dell'indice al primo segnale di interesse
  var warmed = false;
  function warm() {
    if (warmed) return;
    warmed = true;
    getIndex().catch(function () {});
  }
  root.addEventListener('mouseenter', warm, { once: true });
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { warm(); io.disconnect(); }
    }, { rootMargin: '400px' });
    io.observe(root);
  }

  // Il radar è dimensionato sul contenitore: al cambio di larghezza
  // (rotazione dello schermo, resize) va ridisegnato.
  var resizeTimer;
  if (window.addEventListener) window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (loaded().length < 2) return;
      var w = els.radar.clientWidth || 0;
      if (Math.abs(w - radarWidth) > 24) renderRadar();
    }, 180);
  });

  /* ==================================================================
     12. AVVIO
     ================================================================== */
  /* API di debug: apri la console e scrivi CypherCompare.state
     per vedere profilo e punteggi calcolati su un PC. */
  window.CypherCompare = {
    state: state,
    analyze: analyze,
    canonicalize: canonicalize,
    buildProfile: buildProfile,
    computeScores: computeScores,
    setSlot: setSlot,
    render: render
  };

  render();
  var fromUrl = readURL();
  if (fromUrl.length) {
    fromUrl.reduce(function (chain, h, i) {
      return chain.then(function () { return setSlot(i + 1, h); });
    }, Promise.resolve());
  }
})();
